import { ReactNode } from 'react';
import { differenceInBusinessDays } from 'date-fns';
import { useLang } from 'hooks/lang';

import { Column, LinearProgress, Tooltip } from 'components/Blocks';

import { formatDate } from 'utils/date';
import { cn, CSS_COLORS } from 'styles';

type Target = {
  expectedDate?: string;
  startedAt?: string;
  createdAt?: string;
  finished?: boolean;
  doneAt?: boolean;
};

type DeadlineProps = {
  className?: string;
  text?: ReactNode;
  target: Target;
};

function parseProgress({ expectedDate, startedAt, createdAt, finished, doneAt }: Target): {
  value: number;
  color?: string;
} {
  if (finished || doneAt) return { value: 100, color: '#00D39D' };

  if (!expectedDate) return { value: -5 };

  const daysLeft = differenceInBusinessDays(new Date(expectedDate), new Date());

  if (daysLeft <= 0) return { value: 100, color: daysLeft === 0 ? '#FFBF00' : '#F3003A' };

  const fullPeriod = differenceInBusinessDays(
    new Date(expectedDate),
    new Date((startedAt || createdAt) as string),
  );

  const elapsedTime = ((fullPeriod - daysLeft) / fullPeriod) * 100;

  // determine which values trigger warning
  return { value: elapsedTime };
}

export function Deadline({ target, text, ...props }: DeadlineProps) {
  const { t } = useLang();

  const { value, color } = parseProgress(target);

  const expectedDate = target?.expectedDate;

  return (
    <Tooltip disabled={value < 0 || value > 100} content={`${value.toFixed(2)}%`} ensurePresence>
      <Column gap="1" alignItems="center" fullWidth>
        <span className="whitespace-nowrap text-center text-xs">
          {text ??
            (expectedDate ? `${t('due-to')} ${formatDate(expectedDate)}` : t('no-date-given'))}
        </span>

        <LinearProgress
          {...props}
          className={cn('h-2.5', props.className)}
          color={color ?? CSS_COLORS.accent.foreground}
          value={value}
        />
      </Column>
    </Tooltip>
  );
}
